/**
 * Lightweight DOM tooltip used by panels that can't rely on the
 * native ``title`` attribute.  Two pain points the native one has:
 *
 *   * Browsers wait 500-1500 ms before showing the popup, which
 *     reads as "broken" on a dense dashboard where users sweep over
 *     numbers quickly.
 *   * Some extensions / accessibility tools intercept hover events,
 *     leaving the ``cursor: help`` visible but suppressing the
 *     tooltip entirely.  Mac users see this most often.
 *   * Native ``title`` collapses newlines and renders flat single-
 *     line text; multi-row content (e.g. "ticker x weight x
 *     notional" per pod) becomes an unreadable jam.
 *
 * Our tooltip:
 *   * 120 ms show delay (felt instant but cheap to dismiss false
 *     hovers).
 *   * Mounted to ``document.body`` via a portal so panel overflow
 *     can't clip it.
 *   * Multi-line ``content`` -- pass an array of strings, get a
 *     pre-line block.
 *   * Auto-flips above/below the trigger to stay within the
 *     viewport.
 *   * Optional ``cursor`` prop (defaults to ``help``) so the
 *     question-mark cursor matches the popup affordance.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

const SHOW_DELAY_MS = 120;
const TOOLTIP_OFFSET_PX = 8;

interface TooltipProps {
  /** Text or array of lines.  Array members render on separate lines. */
  content: string | ReadonlyArray<string>;
  children: ReactNode;
  /** Pass-through wrapper style; ``cursor: 'help'`` by default. */
  style?: CSSProperties;
  /** Render trigger as ``span`` (default) or ``div``.  ``div`` makes
   *  the wrapper a block element -- useful when wrapping a list row. */
  as?: 'span' | 'div';
  /** Tooltip max width in px; default 280. */
  maxWidth?: number;
}

interface TooltipPosition {
  left: number;
  top: number;
  /** Whether the tooltip flipped to render *above* the trigger -- the
   *  caller uses this to mirror the arrow.  Currently unused (no
   *  arrow drawn) but the bookkeeping is cheap. */
  above: boolean;
}

export function Tooltip({
  content,
  children,
  style,
  as = 'span',
  maxWidth = 280,
}: TooltipProps): ReactNode {
  const triggerRef = useRef<HTMLElement | null>(null);
  const showTimerRef = useRef<number | null>(null);
  const [pos, setPos] = useState<TooltipPosition | null>(null);

  const compute = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Default: below the trigger, centred horizontally.  Flip above
    // when there isn't ~80 px of clearance below.
    const below = window.innerHeight - r.bottom > 80;
    const top = below
      ? r.bottom + TOOLTIP_OFFSET_PX
      : r.top - TOOLTIP_OFFSET_PX;
    // Clamp horizontally so a near-edge trigger doesn't render the
    // tooltip half-offscreen.  ``maxWidth`` is the design cap.
    const halfW = Math.min(maxWidth, 280) / 2;
    let centreX = r.left + r.width / 2;
    centreX = Math.max(halfW + 8, Math.min(window.innerWidth - halfW - 8, centreX));
    setPos({ left: centreX, top, above: !below });
  }, [maxWidth]);

  const handleEnter = useCallback(() => {
    // 120 ms latch -- enough to dismiss the inevitable cursor-sweep
    // hovers across a row of dense numbers.
    if (showTimerRef.current !== null) window.clearTimeout(showTimerRef.current);
    showTimerRef.current = window.setTimeout(() => {
      compute();
    }, SHOW_DELAY_MS);
  }, [compute]);

  const handleLeave = useCallback(() => {
    if (showTimerRef.current !== null) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    setPos(null);
  }, []);

  // Touch / pen: mouseenter is unreliable on the first tap (iOS
  // Safari synthesizes it but immediately fires mouseleave around
  // the same touchstart, which dismisses the popup before render).
  // Handle pointerdown explicitly: show immediately, then dismiss
  // on the next pointerdown anywhere outside the trigger.
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      if (showTimerRef.current !== null) {
        window.clearTimeout(showTimerRef.current);
        showTimerRef.current = null;
      }
      compute();
    },
    [compute],
  );

  // Cleanup any pending timer on unmount.
  useEffect(
    () => () => {
      if (showTimerRef.current !== null) window.clearTimeout(showTimerRef.current);
    },
    [],
  );

  // While the tooltip is visible from a touch tap, the next
  // pointerdown anywhere outside the trigger should dismiss it.
  // We only install this listener while the popup is open so we
  // don't waste cycles on every page tap.
  useEffect(() => {
    if (pos === null) return;
    const onDocPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
      const el = triggerRef.current;
      if (el && el.contains(e.target as Node)) return; // tap inside -- keep
      setPos(null);
    };
    document.addEventListener('pointerdown', onDocPointerDown, { capture: true });
    return () =>
      document.removeEventListener('pointerdown', onDocPointerDown, { capture: true });
  }, [pos]);

  const lines = Array.isArray(content) ? content : [content];
  // Filter empty strings so callers can use conditional lines without
  // generating an empty row.
  const visibleLines = lines.filter((l) => l && l.length > 0);
  if (visibleLines.length === 0) {
    // Nothing to show -- render children straight without the trigger
    // wrapper.  Keeps the DOM clean for "tooltip optional" cases.
    return <>{children}</>;
  }

  const triggerStyle: CSSProperties = {
    cursor: 'help',
    ...style,
  };

  const triggerProps = {
    ref: triggerRef as React.RefObject<HTMLElement>,
    onMouseEnter: handleEnter,
    onMouseLeave: handleLeave,
    onPointerDown: handlePointerDown,
    onFocus: handleEnter,
    onBlur: handleLeave,
    style: triggerStyle,
  } as const;

  const tooltipNode =
    pos !== null && typeof document !== 'undefined'
      ? createPortal(
          <div
            role="tooltip"
            style={{
              position: 'fixed',
              left: pos.left,
              top: pos.top,
              transform: pos.above ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
              background: '#161b22',
              color: '#e6edf3',
              border: '1px solid #30363d',
              borderRadius: 6,
              padding: '6px 10px',
              fontSize: 11,
              lineHeight: 1.45,
              maxWidth,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.55)',
              whiteSpace: 'pre-wrap',
              pointerEvents: 'none',
              zIndex: 9999,
            }}
          >
            {visibleLines.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>,
          document.body,
        )
      : null;

  if (as === 'div') {
    return (
      <>
        <div {...(triggerProps as React.HTMLAttributes<HTMLDivElement>)}>
          {children}
        </div>
        {tooltipNode}
      </>
    );
  }
  return (
    <>
      <span {...(triggerProps as React.HTMLAttributes<HTMLSpanElement>)}>
        {children}
      </span>
      {tooltipNode}
    </>
  );
}
